<template>
  <div class="world-editor-map">
    <!-- Canvas controls -->
    <div class="map-controls gm-panel">
      <button
          :class="['control-btn', { 'is-active': dragMode }]"
          :title="dragMode ? 'Pan the map by dragging. Click to switch to selecting rooms.' : 'Select rooms by clicking. Click to switch to panning.'"
          @click="toggleDragMode"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path v-if="dragMode" d="M8 1.5 5.5 4h5L8 1.5Zm0 13L5.5 12h5L8 14.5ZM1.5 8 4 5.5v5L1.5 8Zm13 0L12 10.5v-5L14.5 8Z"/>
          <path v-else d="M3.5 1.5 12 7.2l-3.4.8L10 11.6l-1.7.7-1.4-3.5-2.4 2.3V1.5Z"/>
        </svg>
        <span>{{ dragMode ? 'Pan' : 'Select' }}</span>
      </button>
      <span class="control-divider"></span>
      <button class="control-btn" title="Centre the view on the map" @click="centerMap">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 1v3M8 12v3M1 8h3M12 8h3"/>
          <circle cx="8" cy="8" r="3.2"/>
        </svg>
        <span>Centre</span>
      </button>
      <button class="control-btn" title="Zoom out until every room on this floor is visible" @click="fitToContent">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M2 5.5V2h3.5M14 5.5V2h-3.5M2 10.5V14h3.5M14 10.5V14h-3.5"/>
        </svg>
        <span>Fit floor</span>
      </button>
      <span class="control-divider"></span>
      <span class="control-zoom" title="Current zoom">{{ Math.round(zoom * 100) }}%</span>
    </div>

    <!-- Main SVG Map -->
    <svg
        ref="mapSvg"
        class="world-map-svg"
        @click="onSvgClick"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @wheel.prevent="onWheel"
    >
      <!-- Pattern definitions -->
      <defs>
        <pattern id="worldEditorTexture" patternUnits="userSpaceOnUse" width="1050" height="1050">
          <image href="@/assets/images/backgrounds/world-editor-texture.png" width="1050" height="1050"/>
        </pattern>
        <!-- One cell. Tiling it means the grid never runs out under a pan. -->
        <pattern
            id="worldEditorGrid"
            :width="cellSize"
            :height="cellSize"
            patternUnits="userSpaceOnUse"
        >
          <path
              :d="`M ${cellSize} 0 L 0 0 0 ${cellSize}`"
              :stroke="gridColor"
              fill="none"
              stroke-width="0.6"
          />
        </pattern>
        <!-- Every fifth line is heavier, so distance on the map can be counted
             rather than estimated. -->
        <pattern
            id="worldEditorGridMajor"
            :width="cellSize * 5"
            :height="cellSize * 5"
            patternUnits="userSpaceOnUse"
        >
          <rect :width="cellSize * 5" :height="cellSize * 5" fill="url(#worldEditorGrid)"/>
          <path
              :d="`M ${cellSize * 5} 0 L 0 0 0 ${cellSize * 5}`"
              :stroke="gridColorMajor"
              fill="none"
              stroke-width="1"
          />
        </pattern>
      </defs>

      <!-- Transform group for pan/zoom -->
      <g :transform="finalTransform">
        <!-- Background texture -->
        <rect :x="-fieldExtent / 2" :y="-fieldExtent / 2" :width="fieldExtent" :height="fieldExtent"
              fill="url(#worldEditorTexture)" opacity="0.28"/>
        <!-- Grid -->
        <rect
            v-if="showGrid"
            class="grid-layer"
            :x="-fieldExtent / 2"
            :y="-fieldExtent / 2"
            :width="fieldExtent"
            :height="fieldExtent"
            fill="url(#worldEditorGridMajor)"
        />

        <!-- Connections -->
        <g class="connections-layer">
          <line
              v-for="connection in visibleConnections"
              :key="'conn-' + connection.id"
              :class="['connection', { 'connection-vertical': connection.isVertical }]"
              :stroke="getConnectionColor(connection)"
              :stroke-width="getConnectionWidth(connection)"
              :x1="getConnectionStartX(connection)"
              :x2="getConnectionEndX(connection)"
              :y1="getConnectionStartY(connection)"
              :y2="getConnectionEndY(connection)"
              @click.stop="onConnectionClick(connection)"
          />
        </g>

        <g class="rooms-layer">
          <g
              v-for="room in visibleRooms"
              :key="'room-' + room.id"
              :class="['room-group', { 'room-selected': isRoomSelected(room.id) }]"
              @click.stop="onRoomClick(room, $event)"
              @mousedown.stop="onRoomMouseDown(room, $event)"
          >
            <!-- Room base -->
            <WorldEditorRoom
                :activeLayers="activeLayers"
                :cellPadding="cellPadding"
                :cellSize="cellSize"
                :editorState="editorState"
                :room="room"
                :selected="isRoomSelected(room.id)"
                :showName="showRoomNames"
                :verticalLink="verticalLinks[room.id] || NO_VERTICAL_LINK"
                @show-entity-details="onShowEntityDetails"
            />
          </g>
        </g>

        <!-- Tool overlays -->
        <g v-if="isEditMode" class="tool-overlay">
          <!-- Connection preview -->
          <line
              v-if="connectionPreview"
              :x1="connectionPreview.startX"
              :x2="connectionPreview.endX"
              :y1="connectionPreview.startY"
              :y2="connectionPreview.endY"
              class="connection-preview"
              stroke="var(--gm-accent-hi)"
              stroke-dasharray="5,5"
              stroke-width="2"
          />

          <!-- Room creation preview -->
          <rect
              v-if="roomCreationPreview"
              :height="cellSize - cellPadding"
              :width="cellSize - cellPadding"
              :x="roomCreationPreview.x"
              :y="roomCreationPreview.y"
              class="room-creation-preview"
              fill="var(--gm-accent-soft)"
              stroke="var(--gm-accent-hi)"
              stroke-dasharray="5,5"
              stroke-width="2"
          />
        </g>
      </g>
    </svg>

    <!-- Canvas overlays, gathered on the right edge so the map itself stays clear -->
    <div class="map-overlays">
      <div class="current-turn-container">
        <CurrentTurnComponent :currentTurn="currentCycleNumber"/>
      </div>

      <WorldEditorFloorSwitcher
          :currentFloor="currentFloor"
          @floor-up="onFloorUp"
          @floor-down="onFloorDown"
      />

      <div v-if="showMinimap" class="minimap gm-panel gm-panel--flush">
        <div class="minimap-head">
          <span class="gm-eyebrow">Floor {{ currentFloor }}</span>
          <button class="minimap-collapse" title="Hide the minimap" @click="showMinimap = false">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>
          </button>
        </div>
        <div class="minimap-body">
          <WorldEditorMinimap
              :currentFloor="currentFloor"
              :rooms="allRooms"
              :viewBounds="viewBounds"
              @navigate="onMinimapNavigate"
          />
        </div>
      </div>
      <button v-else class="minimap-restore" title="Show the minimap" @click="showMinimap = true">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="2" y="2" width="12" height="12" rx="2"/>
          <rect x="5" y="5" width="6" height="6" rx="1"/>
        </svg>
        <span>Minimap</span>
      </button>
    </div>
  </div>
</template>

<script>
import {WorldEditorConfig, WorldEditorLayer, WorldEditorTool} from '@/models/WorldEditorModels.ts';
import WorldEditorRoom from './WorldEditorRoom.vue';
import WorldEditorMinimap from './WorldEditorMinimap.vue';
import CurrentTurnComponent from '@/components/Game/CurrentTurnComponent.vue';
import WorldEditorFloorSwitcher from './WorldEditorFloorSwitcher.vue';

export default {
  name: 'WorldEditorMap',
  components: {
    WorldEditorRoom,
    WorldEditorMinimap,
    CurrentTurnComponent,
    WorldEditorFloorSwitcher
  },
  props: {
    mapData: {
      type: Object,
      default: () => ({})
    },
    editorState: {
      type: Object,
      required: true
    },
    currentFloor: {
      type: Number,
      default: 1
    },
    currentSubLocationId: {
      type: String,
      default: null
    },
    currentCycleNumber: {
      type: Number,
      default: null
    }
  },
  emits: [
    'room-selected',
    'room-created',
    'room-moved',
    'room-deleted',
    'connection-created',
    'connection-deleted',
    'connection-selected',
    'map-clicked',
    'show-entity-details',
    'layer-toggled',
    'refresh-map',
    'floor-up',
    'floor-down',
  ],
  data() {
    return {
      // Pan & Zoom
      panX: 0,
      panY: 0,
      zoom: 1,
      isDragging: false,
      dragStart: {x: 0, y: 0},

      // Debouncing for performance
      lastUpdateTime: 0,
      debounceDelay: 50,

      // SVG readiness and viewBounds caching
      svgReady: false,
      cachedViewBounds: null,

      // Set once the viewport has been positioned deliberately rather than restored
      viewProgrammaticallySet: false,

      // For triggering viewBounds recalculation on resize
      windowSize: {
        width: window.innerWidth,
        height: window.innerHeight
      },

      // Interaction state
      dragMode: true,
      selectedRoomForConnection: null,
      connectionPreview: null,
      roomCreationPreview: null,

      // UI settings
      showGrid: true,
      showMinimap: true,
      showLayerIndicators: true,

      // Make WorldEditorLayer available in template
      WorldEditorLayer,

      // Shared, so a room without stairs never falls back to the per-room scan.
      NO_VERTICAL_LINK: Object.freeze({up: false, down: false}),

      // Configuration
      cellSize: WorldEditorConfig.cellSize,
      cellPadding: WorldEditorConfig.cellPadding,
      gridColor: WorldEditorConfig.gridColor
    };
  },
  computed: {
    isEditMode() {
      return this.editorState?.mode === 'edit';
    },

    selectedTool() {
      return this.editorState?.selectedTool || WorldEditorTool.SELECT;
    },

    activeLayers() {
      return this.editorState?.activeLayers || new Set();
    },

    allRooms() {
      return Array.from(this.editorState?.rooms?.values() || []);
    },

    inCurrentSubLocation() {
      // No selection means show everything, so an editor without sub-locations still works.
      if (!this.currentSubLocationId) {
        return () => true;
      }
      return room => room.subLocationId === this.currentSubLocationId;
    },

    visibleRooms() {
      return this.allRooms.filter(room =>
          room.position.grid_z === this.currentFloor && this.inCurrentSubLocation(room));
    },

    visibleConnections() {
      return Array.from(this.editorState?.connections?.values() || [])
          .filter(connection => {
            const fromRoom = this.editorState.rooms.get(connection.fromRoomId);
            const toRoom = this.editorState.rooms.get(connection.toRoomId);
            return fromRoom && toRoom &&
                fromRoom.position.grid_z === this.currentFloor &&
                toRoom.position.grid_z === this.currentFloor &&
                this.inCurrentSubLocation(fromRoom) &&
                this.inCurrentSubLocation(toRoom) &&
                !connection.isVertical;
          });
    },

    // Stair markers used to be derived per room, and each room walked the whole
    // connection set to do it — 175 rooms x 647 connections on a busy floor.
    // Walking the connections once here makes it a single pass.
    verticalLinks() {
      const links = {};
      const rooms = this.editorState?.rooms;
      if (!rooms) return links;

      this.editorState.connections?.forEach(connection => {
        if (!connection.isVertical) return;

        const from = rooms.get(connection.fromRoomId);
        const to = rooms.get(connection.toRoomId);
        if (!from || !to) return;

        const rising = to.position.grid_z > from.position.grid_z;
        const fromKey = rising ? 'up' : 'down';
        const toKey = rising ? 'down' : 'up';

        (links[from.id] || (links[from.id] = {up: false, down: false}))[fromKey] = true;
        (links[to.id] || (links[to.id] = {up: false, down: false}))[toKey] = true;
      });

      return links;
    },

    // Room names are unreadable once the map is zoomed out past roughly a third,
    // so they are not drawn — which also keeps a few hundred text nodes out of
    // the tree on the floors that have them.
    showRoomNames() {
      return this.zoom >= 0.55;
    },

    // The tiled grid and texture are painted over one large square inside the
    // pan/zoom group, so panning never reaches an edge.
    fieldExtent() {
      return this.cellSize * 2000;
    },

    gridColorMajor() {
      return 'rgba(250, 218, 149, 0.22)';
    },

    bounds() {
      if (!this.visibleRooms.length) {
        return {minX: 0, maxX: 0, minY: 0, maxY: 0};
      }

      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

      this.visibleRooms.forEach(room => {
        const {grid_x, grid_y} = room.position;
        if (grid_x < minX) minX = grid_x;
        if (grid_x > maxX) maxX = grid_x;
        if (grid_y < minY) minY = grid_y;
        if (grid_y > maxY) maxY = grid_y;
      });

      return {minX, maxX, minY, maxY};
    },


    finalTransform() {
      return `translate(${this.panX}, ${this.panY}) scale(${this.zoom})`;
    },

    viewBounds() {
      // Return cached bounds if SVG isn't ready yet
      if (!this.svgReady || !this.$refs.mapSvg) {
        return this.cachedViewBounds;
      }

      try {
        const rect = this.$refs.mapSvg.getBoundingClientRect();

        // Ensure we have valid dimensions
        if (!rect || rect.width === 0 || rect.height === 0) {
          return this.cachedViewBounds;
        }

        const factor = 1 / this.zoom;
        const bounds = {
          x: (-this.panX) * factor,
          y: (-this.panY) * factor,
          width: rect.width * factor,
          height: rect.height * factor,
          // Include dependencies to trigger reactivity
          _windowWidth: this.windowSize.width,
          _windowHeight: this.windowSize.height,
          _zoom: this.zoom,
          _panX: this.panX,
          _panY: this.panY
        };

        // Cache the valid bounds
        this.cachedViewBounds = bounds;
        return bounds;

      } catch (error) {
        console.warn('Error calculating viewBounds:', error);
        return this.cachedViewBounds;
      }
    }
  },
  mounted() {
    // Wait for next tick to ensure DOM is ready
    this.$nextTick(() => {
      // Set initial view bounds
      this.initializeViewBounds();

      // Mark SVG as ready
      this.svgReady = true;

      // Load saved map state from localStorage if available
      const savedMapState = localStorage.getItem('worldEditor_mapState');
      if (savedMapState) {
        try {
          // A programmatic centring (e.g. aligning to the current character) may already have
          // run — this restore is deferred, so it must not clobber a deliberate viewport.
          if (this.viewProgrammaticallySet) {
            return;
          }
          const mapState = JSON.parse(savedMapState);
          this.panX = mapState.panX;
          this.panY = mapState.panY;
          this.zoom = mapState.zoom;
        } catch (error) {
          console.error('Failed to parse saved map state:', error);
          // Center the map if loading saved state fails
          this.centerMap();
        }
      } else {
        // Center the map if no saved state
        this.centerMap();
      }
    });

    // Add resize event listener to update viewBounds
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    currentSubLocationId() {
      // The visible subset changed entirely; the old pan/zoom points at rooms that are gone.
      this.$nextTick(() => this.fitToContent());
    }
  },
  methods: {
    centerOnRoom(room) {
      if (!room) return;

      const rect = this.$refs.mapSvg?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;

      this.panX = rect.width / 2 - room.position.grid_x * this.cellSize * this.zoom;
      this.panY = rect.height / 2 - room.position.grid_y * this.cellSize * this.zoom;
      this.viewProgrammaticallySet = true;

      this.saveMapState();

      if (this.svgReady) {
        this.initializeViewBounds();
      }
    },

    // Save map state to localStorage
    saveMapState() {
      const mapState = {
        panX: this.panX,
        panY: this.panY,
        zoom: this.zoom
      };
      localStorage.setItem('worldEditor_mapState', JSON.stringify(mapState));
    },

    // Initialize view bounds safely
    initializeViewBounds() {
      try {
        const rect = this.$refs.mapSvg?.getBoundingClientRect();
        if (rect && rect.width > 0 && rect.height > 0) {
          const factor = 1 / this.zoom;
          this.cachedViewBounds = {
            x: (-this.panX) * factor,
            y: (-this.panY) * factor,
            width: rect.width * factor,
            height: rect.height * factor
          };
        }
      } catch (error) {
        console.warn('Error initializing viewBounds:', error);
      }
    },

    // Handle window resize with debouncing
    handleResize() {
      const now = Date.now();
      if (now - this.lastUpdateTime > this.debounceDelay) {
        this.lastUpdateTime = now;
        requestAnimationFrame(() => {
          // Update windowSize to trigger viewBounds recalculation
          this.windowSize = {
            width: window.innerWidth,
            height: window.innerHeight
          };

          // Recalculate view bounds
          if (this.svgReady) {
            this.initializeViewBounds();
          }
        });
      }
    },

    // Room positioning
    getRoomLeft(room) {
      return room.position.grid_x * this.cellSize + this.cellPadding / 2;
    },

    getRoomTop(room) {
      return room.position.grid_y * this.cellSize + this.cellPadding / 2;
    },

    getRoomCenterX(room) {
      return room.position.grid_x * this.cellSize + this.cellSize / 2;
    },

    getRoomCenterY(room) {
      return room.position.grid_y * this.cellSize + this.cellSize / 2;
    },

    getRoomBottom(room) {
      return this.getRoomTop(room) + this.cellSize - this.cellPadding;
    },


    // Connection positioning
    getConnectionStartX(connection) {
      const room = this.editorState.rooms.get(connection.fromRoomId);
      return room ? this.getRoomCenterX(room) : 0;
    },

    getConnectionStartY(connection) {
      const room = this.editorState.rooms.get(connection.fromRoomId);
      return room ? this.getRoomCenterY(room) : 0;
    },

    getConnectionEndX(connection) {
      const room = this.editorState.rooms.get(connection.toRoomId);
      return room ? this.getRoomCenterX(room) : 0;
    },

    getConnectionEndY(connection) {
      const room = this.editorState.rooms.get(connection.toRoomId);
      return room ? this.getRoomCenterY(room) : 0;
    },

    getConnectionColor(connection) {
      return connection.isVertical ?
          WorldEditorConfig.connectionColors.vertical :
          WorldEditorConfig.connectionColors.normal;
    },

    getConnectionWidth(connection) {
      return connection.isVertical ? 3 : 2;
    },

    // Layer management
    getActiveRoomLayers(room) {
      const layers = [];
      Object.values(WorldEditorLayer).forEach(layer => {
        if (this.activeLayers.has(layer) && room.hasLayerEntities(layer)) {
          layers.push(layer);
        }
      });
      return layers;
    },

    getLayerColor(layer) {
      return WorldEditorConfig.layerColors[layer] || '#888';
    },

    // Selection
    isRoomSelected(roomId) {
      return this.editorState?.selectedRooms?.has(roomId) || false;
    },

    // Mouse events
    onMouseDown(e) {
      if (!this.dragMode) return;

      this.isDragging = true;
      this.dragStart.x = e.clientX - this.panX;
      this.dragStart.y = e.clientY - this.panY;
    },

    onMouseMove(e) {
      if (this.isDragging && this.dragMode) {
        this.panX = e.clientX - this.dragStart.x;
        this.panY = e.clientY - this.dragStart.y;

        // Update view bounds during pan
        if (this.svgReady) {
          this.initializeViewBounds();
        }

        // Save map state to localStorage
        this.saveMapState();
      }

      // Update tool previews
      this.updateToolPreviews(e);
    },

    onMouseUp() {
      this.isDragging = false;
    },

    onWheel(e) {
      if (!this.dragMode) return;

      const direction = e.deltaY < 0 ? 1 : -1;
      let newZoom = this.zoom + direction * 0.1;
      newZoom = Math.max(0.2, Math.min(5, newZoom));

      // Zoom around mouse position
      const rect = this.$refs.mapSvg.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const ratio = newZoom / this.zoom;

      this.panX = mx - (mx - this.panX) * ratio;
      this.panY = my - (my - this.panY) * ratio;
      this.zoom = newZoom;

      // Update view bounds after zoom
      if (this.svgReady) {
        this.initializeViewBounds();
      }

      // Save map state to localStorage
      this.saveMapState();
    },

    // Click events
    onSvgClick(e) {
      if (this.isDragging) return;

      const [mapX, mapY] = this.screenToMap(e.clientX, e.clientY);
      const gridX = Math.floor(mapX / this.cellSize);
      const gridY = Math.floor(mapY / this.cellSize);

      this.$emit('map-clicked', {
        gridX,
        gridY,
        gridZ: this.currentFloor,
        screenX: e.clientX,
        screenY: e.clientY
      });
    },

    onRoomClick(room, event) {
      this.handleRoomInteraction(room, event);
    },

    onRoomMouseDown(room, e) {
      if (this.selectedTool === WorldEditorTool.MOVE_ROOM) {
        // Start room dragging
        this.startRoomDrag(room, e);
      }
    },

    onConnectionClick(connection) {
      if (this.selectedTool === WorldEditorTool.DISCONNECT_ROOMS) {
        this.$emit('connection-deleted', connection.id);
      } else {
        // Emit connection-selected event for interactive connections
        this.$emit('connection-selected', connection);
      }
    },

    // Tool-specific interactions
    handleRoomInteraction(room, event) {
      switch (this.selectedTool) {
        case WorldEditorTool.SELECT:
          this.$emit('room-selected', room, event?.shiftKey || false);
          break;

        case WorldEditorTool.DELETE_ROOM:
          this.$emit('room-deleted', room);
          break;

        case WorldEditorTool.CONNECT_ROOMS:
          this.handleRoomConnection(room);
          break;

        default:
          this.$emit('room-selected', room, event?.shiftKey || false);
      }
    },

    handleRoomConnection(room) {
      if (!this.selectedRoomForConnection) {
        // First room selected for connection
        this.selectedRoomForConnection = room;
        this.updateConnectionPreview();
      } else if (this.selectedRoomForConnection.id !== room.id) {
        // Second room selected, create connection
        this.$emit('connection-created', this.selectedRoomForConnection.id, room.id);
        this.selectedRoomForConnection = null;
        this.connectionPreview = null;
      }
    },

    startRoomDrag(room, e) {
      // Implementation for room dragging would go here
      // This is a placeholder for the room moving functionality
    },

    // Tool previews
    updateToolPreviews(e) {
      if (!this.isEditMode) return;

      const [mapX, mapY] = this.screenToMap(e.clientX, e.clientY);

      switch (this.selectedTool) {
        case WorldEditorTool.CREATE_ROOM:
          this.updateRoomCreationPreview(mapX, mapY);
          break;

        case WorldEditorTool.CONNECT_ROOMS:
          this.updateConnectionPreview(mapX, mapY);
          break;

        default:
          this.clearPreviews();
      }
    },

    updateRoomCreationPreview(mapX, mapY) {
      const gridX = Math.floor(mapX / this.cellSize);
      const gridY = Math.floor(mapY / this.cellSize);

      // Check if room already exists at this position
      const existingRoom = this.visibleRooms.find(room =>
          room.position.grid_x === gridX && room.position.grid_y === gridY
      );

      if (!existingRoom) {
        this.roomCreationPreview = {
          x: gridX * this.cellSize + this.cellPadding / 2,
          y: gridY * this.cellSize + this.cellPadding / 2
        };
      } else {
        this.roomCreationPreview = null;
      }
    },

    updateConnectionPreview(mapX, mapY) {
      if (this.selectedRoomForConnection) {
        this.connectionPreview = {
          startX: this.getRoomCenterX(this.selectedRoomForConnection),
          startY: this.getRoomCenterY(this.selectedRoomForConnection),
          endX: mapX,
          endY: mapY
        };
      }
    },

    clearPreviews() {
      this.roomCreationPreview = null;
      this.connectionPreview = null;
    },

    // Cancel all in-progress actions (called when Escape key is pressed)
    cancelInProgressActions() {
      // Clear connection creation state
      this.selectedRoomForConnection = null;

      // Clear all previews
      this.clearPreviews();

      // Reset drag mode if needed
      this.dragMode = true;
    },

    // Coordinate conversion
    screenToMap(clientX, clientY) {
      const rect = this.$refs.mapSvg.getBoundingClientRect();
      const sx = clientX - rect.left;
      const sy = clientY - rect.top;
      const unTranslateX = sx - this.panX;
      const unTranslateY = sy - this.panY;
      const factor = 1 / this.zoom;
      return [unTranslateX * factor, unTranslateY * factor];
    },

    // Map navigation
    toggleDragMode() {
      this.dragMode = !this.dragMode;
      this.clearPreviews();
      this.selectedRoomForConnection = null;
    },

    centerMap() {
      if (this.visibleRooms.length === 0) {
        this.panX = 0;
        this.panY = 0;
        // Save map state to localStorage
        this.saveMapState();
        return;
      }

      const {minX, maxX, minY, maxY} = this.bounds;
      const centerX = (minX + maxX) / 2 * this.cellSize;
      const centerY = (minY + maxY) / 2 * this.cellSize;

      const rect = this.$refs.mapSvg?.getBoundingClientRect();
      if (rect && rect.width > 0 && rect.height > 0) {
        this.panX = rect.width / 2 - centerX * this.zoom;
        this.panY = rect.height / 2 - centerY * this.zoom;

        // Update view bounds after centering
        if (this.svgReady) {
          this.initializeViewBounds();
        }

        // Save map state to localStorage
        this.saveMapState();
      }
    },

    fitToContent() {
      if (this.visibleRooms.length === 0) return;

      const {minX, maxX, minY, maxY} = this.bounds;
      const contentWidth = (maxX - minX + 2) * this.cellSize;
      const contentHeight = (maxY - minY + 2) * this.cellSize;

      const rect = this.$refs.mapSvg?.getBoundingClientRect();
      if (rect && rect.width > 0 && rect.height > 0) {
        const scaleX = rect.width / contentWidth;
        const scaleY = rect.height / contentHeight;
        this.zoom = Math.min(scaleX, scaleY, 2); // Max zoom of 2x

        // Save map state to localStorage before centerMap
        this.saveMapState();

        this.centerMap();

        // Update view bounds after fitting
        if (this.svgReady) {
          this.initializeViewBounds();
        }
      }
    },

    onMinimapNavigate(position) {
      // Navigate to position from minimap
      const rect = this.$refs.mapSvg?.getBoundingClientRect();
      if (rect) {
        this.panX = rect.width / 2 - position.x * this.zoom;
        this.panY = rect.height / 2 - position.y * this.zoom;

        // Update view bounds after navigation
        if (this.svgReady) {
          this.initializeViewBounds();
        }
      }
    },

    onShowEntityDetails(details) {
      // Emit the event up to the parent component to handle
      this.$emit('show-entity-details', details);

      // Optionally, select the room that contains these entities
      if (details.roomId) {
        const room = this.visibleRooms.find(r => r.id === details.roomId);
        if (room) {
          this.onRoomClick(room);
        }
      }
    },

    // Floor switcher methods
    onFloorUp() {
      this.$emit('floor-up');
    },

    onFloorDown() {
      this.$emit('floor-down');
    }
  }
};
</script>

<style scoped>
.world-editor-map {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--gm-surface-0);
  overflow: hidden;
}

/* ---------- canvas controls ---------- */

.map-controls {
  position: absolute;
  top: var(--gm-gap);
  left: var(--gm-gap);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
  padding: 0.2rem;
  background: rgba(24, 27, 28, 0.92);
  backdrop-filter: blur(6px);
  box-shadow: var(--gm-shadow);
}

.control-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.55rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text-muted);
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-control);
  font-weight: 600;
  letter-spacing: var(--gm-track-tight);
  text-transform: uppercase;
  cursor: pointer;
  transition: color var(--gm-duration) var(--gm-ease),
              background var(--gm-duration) var(--gm-ease),
              border-color var(--gm-duration) var(--gm-ease);
}

.control-btn:hover {
  color: var(--gm-text);
  background: var(--gm-accent-softer);
}

.control-btn:focus-visible {
  outline: none;
  border-color: var(--gm-border-hi);
}

.control-btn.is-active {
  color: var(--gm-accent);
  background: var(--gm-accent-soft);
  border-color: var(--gm-border-dim);
}

.control-btn svg {
  width: 13px;
  height: 13px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

/* The pan/select glyphs are solid shapes rather than outlines. */
.control-btn:first-child svg {
  fill: currentColor;
  stroke: none;
}

.control-divider {
  width: 1px;
  align-self: stretch;
  margin: 0.2rem 0.1rem;
  background: var(--gm-border-dim);
}

.control-zoom {
  padding: 0 0.5rem 0 0.35rem;
  color: var(--gm-text-dim);
  font-family: var(--gm-font-mono);
  font-size: var(--gm-text-control);
  font-variant-numeric: tabular-nums;
}

/* ---------- svg map ---------- */

.world-map-svg {
  width: 100%;
  height: 100%;
  cursor: default;
  display: block;
}

.world-map-svg.dragging {
  cursor: grabbing;
}

.grid-layer {
  pointer-events: none;
}

/* Connections */
.connection {
  cursor: pointer;
  transition: stroke-width var(--gm-duration) var(--gm-ease), stroke var(--gm-duration) var(--gm-ease);
  opacity: 0.7;
}

.connection:hover {
  opacity: 1;
  stroke: var(--gm-accent-hi) !important;
  stroke-width: 3.5 !important;
}

.connection-vertical {
  stroke-dasharray: 4, 3;
}

.connection-preview {
  pointer-events: none;
}

/* Rooms */
.room-group {
  cursor: pointer;
}

/* Tool overlays */
.tool-overlay {
  pointer-events: none;
}

.room-creation-preview {
  pointer-events: none;
}

/* ---------- canvas overlays ---------- */

.map-overlays {
  position: absolute;
  top: var(--gm-gap);
  right: var(--gm-gap);
  bottom: calc(var(--gm-dock-clearance) + var(--gm-gap));
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--gm-gap-sm);
  pointer-events: none;
}

.map-overlays > * {
  pointer-events: auto;
}

.current-turn-container {
  flex: none;
}

.minimap {
  margin-top: auto;
  width: 210px;
  background: rgba(24, 27, 28, 0.94);
  backdrop-filter: blur(6px);
  box-shadow: var(--gm-shadow);
}

.minimap-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gm-gap-sm);
  padding: 0.3rem var(--gm-gap-sm);
  border-bottom: 1px solid var(--gm-border-dim);
}

.minimap-collapse {
  display: inline-flex;
  padding: 0.15rem;
  background: none;
  border: none;
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text-dim);
  cursor: pointer;
}

.minimap-collapse:hover {
  color: var(--gm-accent);
}

.minimap-collapse svg {
  width: 10px;
  height: 10px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
}

.minimap-body {
  height: 140px;
}

.minimap-restore {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  background: rgba(24, 27, 28, 0.92);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  color: var(--gm-text-muted);
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-control);
  font-weight: 600;
  letter-spacing: var(--gm-track-tight);
  text-transform: uppercase;
  cursor: pointer;
}

.minimap-restore:hover {
  color: var(--gm-accent);
  border-color: var(--gm-border);
}

.minimap-restore svg {
  width: 13px;
  height: 13px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.3;
}

@media (max-width: 900px) {
  .map-controls {
    flex-wrap: wrap;
    max-width: calc(100% - 2 * var(--gm-gap));
  }

  .control-btn span {
    display: none;
  }

  .minimap {
    width: 150px;
  }

  .minimap-body {
    height: 100px;
  }
}
</style>